import { FiCheck } from "react-icons/fi";
import InfoTooltip from "newui/components/InfoTooltip";
import {
	StepState,
	STEP_CIRCLE_CLASSES,
	STEP_TEXT_CLASSES,
} from "./stepStyles";

interface StepHeaderItem {
	name: string;
	description?: string;
	enabled: boolean;
}

interface StepperHeaderProps {
	currentStep: number;
	highestStep?: number;
	steps: StepHeaderItem[];
	onStepClick?: (index: number) => void;
}

export const StepperHeader: React.FC<StepperHeaderProps> = ({ steps, currentStep, highestStep: highestStepProp, onStepClick }) => {
	const highestStep = highestStepProp ?? currentStep;

	/**
	 * Collapse step progress into one of three states (PRO-526):
	 *   - viewing   : the step currently on screen
	 *   - completed : a step already passed (before the furthest reached)
	 *   - pending   : everything else (not yet reached, incl. the furthest
	 *                 reached step while an earlier one is being viewed)
	 */
	const getStepState = (index: number): StepState => {
		if (index === currentStep) {
			return "viewing";
		}
		if (index < highestStep) {
			return "completed";
		}
		return "pending";
	};

	const isStepClickable = (index: number): boolean => {
		if (!onStepClick) {
			return false;
		}
		// Any step up to the furthest reached can be revisited, except the one
		// already on screen.
		return index <= highestStep && index !== currentStep;
	};

	return (
		<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-6">
			{steps.map((step, index) => {
				const state = getStepState(index);
				const clickable = isStepClickable(index);
				return (
					<div
						key={index}
						className="flex sm:flex-1 items-center"
					>
						<div className="flex flex-col sm:flex-row items-center sm:flex-1">
							<div
								className={`flex items-center gap-2${clickable ? " cursor-pointer" : ""}`}
								onClick={clickable ? () => onStepClick!(index) : undefined}
								role={clickable ? "button" : undefined}
								tabIndex={clickable ? 0 : undefined}
								onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { onStepClick!(index); } } : undefined}
							>
								<div
									className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium flex-shrink-0 ${STEP_CIRCLE_CLASSES[state]}`}
								>
									{state === "completed" ? (
										<FiCheck size={16} />
									) : (
										<span>{index + 1}</span>
									)}
								</div>
								<div className="relative pr-6">
									<span className={`text-sm ${STEP_TEXT_CLASSES[state]}`}>
										{step.name}
									</span>
									{step.description && (
										<span className="absolute -top-1 right-0">
											<InfoTooltip tooltipText={step.description} size={18} />
										</span>
									)}
								</div>
							</div>
						</div>
						{index < steps.length - 1 && (
							<div className="hidden sm:block h-px flex-1 bg-gray-200 mx-2" />
						)}
					</div>
				);
			})}
		</div>
	);
};
